import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { adminApi } from '@/lib/adminApi';
import { feedbackApi } from '@/lib/feedbackApi';
import { panelList, type Panel } from '@/lib/panels';
import { AdminPage, RegistryTable, FeedbackControl } from '@/admin';

function matches(p: Panel, q: string): boolean {
  const s = q.toLowerCase().trim();
  if (!s) return true;
  return p.panel_id.toLowerCase().includes(s) || (p.label || '').toLowerCase().includes(s) ||
    (p.biomarkers ?? []).some((b) => b.toLowerCase().includes(s));
}

export function PanelsPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['panels'], queryFn: () => adminApi.panels.list() });
  const fb = useQuery({ queryKey: ['feedback-summary', 'panels'], queryFn: () => feedbackApi.summary('panels') });
  const [search, setSearch] = useState('');

  const all = panelList(q.data);
  const rows = all.filter((p) => matches(p, search));
  const refresh = () => void qc.invalidateQueries({ queryKey: ['feedback-summary', 'panels'] });

  return (
    <AdminPage title="Panel Registry" description="Coverage bundles — each panel lists the biomarkers (or instruments) that satisfy it. Open a panel to see its biomarkers and which modes use it.">
      {q.data ? <div className="mb-13 text-body-sm text-text-secondary">Registry version <strong className="text-text-primary">v{String(q.data.version ?? '?')}</strong>{q.data.last_updated ? ` · ${q.data.last_updated}` : ''}</div> : null}
      <RegistryTable<Panel>
        columns={[
          { key: 'panel', header: 'Panel', render: (p) => (
            <div>
              <Link to={`/admin/panels/${encodeURIComponent(p.panel_id)}`} className="font-semibold text-accent hover:underline">{p.label || p.panel_id}</Link>
              <div className="font-mono text-[11px] text-text-muted">{p.panel_id}</div>
            </div>
          ) },
          { key: 'source', header: 'Source', className: 'text-[12px] text-text-secondary', render: (p) => p.source_type || '—' },
          { key: 'biomarkers', header: 'Biomarkers', className: 'font-mono text-[12px]', render: (p) => p.biomarker_count ?? (p.biomarkers?.length ?? 0) },
          { key: 'used', header: 'Used by', className: 'font-mono text-[12px]', render: (p) => (p.used_by ?? []).length || '—' },
          { key: 'recency', header: 'Recency', className: 'text-[12px] text-text-secondary', render: (p) => p.recency_days ? `${p.recency_days}d` : '—' },
        ]}
        rows={rows}
        getRowKey={(p) => p.panel_id}
        count={all.length}
        state={{ loading: q.isLoading, error: q.isError }}
        emptyText={search ? `No panels match "${search}"` : 'No panels.'}
        search={{ value: search, onChange: setSearch, placeholder: 'Search by name, id, or biomarker…' }}
        rowActions={(p) => <FeedbackControl registry="panels" itemId={p.panel_id} itemLabel={p.label || p.panel_id} openCount={fb.data?.[p.panel_id]?.open ?? 0} onChanged={refresh} />}
      />
    </AdminPage>
  );
}
